import { useMemo, useState, useEffect, type ReactNode } from 'react';
import { Crown, ChevronLeft, ChevronRight, Briefcase } from 'lucide-react';

interface Props {
  title: string;             // 게시판 제목 — 예: "대강 (대타 강사) 채용", "구인 정보 (전체)"
  positionValue?: string;    // DB 저장값. 비우면 전체 공고 표시
  jobs: any[];               // 전체 공고 배열 (HomePage가 fetch한 jobPostings)
  loading?: boolean;
  onSelectJob: (job: any) => void;
  pageSize?: number;
  emptyMessage?: string;
  footer?: ReactNode;         // 카드 하단 고정 영역(버튼 등). 루트가 h-full이라 외부 형제로 붙이면
                              // 그리드 stretch 시 넘쳐서 다음 섹션에 가려지므로 내부에서 렌더링한다.
}

export default function JobPositionBoard({
  title,
  positionValue,
  jobs,
  loading = false,
  onSelectJob,
  pageSize = 10,
  emptyMessage,
  footer,
}: Props) {
  const filtered = useMemo(
    () => positionValue
      ? (jobs || []).filter((j: any) => j.position === positionValue)
      : (jobs || []),
    [jobs, positionValue],
  );

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  // 데이터 변동 시 페이지 클램프
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const start = (page - 1) * pageSize;
  const pageItems = filtered.slice(start, start + pageSize);

  const formatDate = (s: string) => {
    const d = new Date(s);
    return `${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getDate().toString().padStart(2, '0')}`;
  };

  // 페이지 번호 노출 범위 (현재 페이지 기준 ±2, 양 끝과 ... 처리)
  const getPageNumbers = (): (number | '…')[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const set = new Set<number>([1, totalPages, page - 1, page, page + 1]);
    const sorted = [...set].filter(n => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    const result: (number | '…')[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('…');
      result.push(sorted[i]);
    }
    return result;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col h-full min-h-[350px]">
      {/* 헤더 */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex items-center justify-between">
        <h3 className="font-bold text-gray-900 flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-primary" />
          {title}
        </h3>
        <span className="text-xs text-gray-500">{filtered.length}건</span>
      </div>

      {/* 테이블 */}
      <div className="flex-1 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs">
            <tr>
              <th className="w-12 px-2 py-2 font-medium">번호</th>
              <th className="px-2 py-2 font-medium text-left">제목</th>
              <th className="hidden sm:table-cell px-2 py-2 font-medium text-left w-32">회사</th>
              <th className="hidden md:table-cell px-2 py-2 font-medium text-left w-40">지역</th>
              <th className="w-16 px-2 py-2 font-medium">등록일</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-gray-400">로딩 중...</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-gray-400">
                  {emptyMessage || '등록된 공고가 없습니다.'}
                </td>
              </tr>
            ) : (
              pageItems.map((job: any, idx: number) => {
                const num = filtered.length - (start + idx); // 최신이 위로 가면서 큰 번호
                // 시/도·시/군/구(work_location_province/district)가 있으면 그대로 쓰고, 없는 과거 데이터는
                // 주소 문자열에서 우편번호를 제거한 뒤 앞 두 토큰으로 대체 표시
                const region = job.work_location_province
                  ? [job.work_location_province, job.work_location_district].filter(Boolean).join(' ')
                  : (job.work_location || job.employer_address || '').replace(/^\(\d+\)\s*/, '').split(' ').slice(0, 2).join(' ');
                return (
                  <tr
                    key={job.id}
                    onClick={() => onSelectJob(job)}
                    className={`cursor-pointer hover:bg-primary/5 transition-colors ${
                      job.is_premium_employer ? 'bg-amber-50/40' : ''
                    }`}
                  >
                    <td className="px-2 py-2 text-center text-xs text-gray-400">{num}</td>
                    <td className="px-2 py-2 text-gray-900">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {job.is_premium_employer && <Crown className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                        <span className="truncate font-medium">{job.title}</span>
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-2 py-2 text-gray-600 truncate">
                      {job.company_name || '-'}
                    </td>
                    <td className="hidden md:table-cell px-2 py-2 text-gray-500 truncate">
                      {region || '-'}
                    </td>
                    <td className="px-2 py-2 text-center text-xs text-gray-500">
                      {formatDate(job.created_at)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {filtered.length > 0 && totalPages > 1 && (
        <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-center gap-1">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-1.5 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="이전 페이지"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          {getPageNumbers().map((n, i) =>
            n === '…' ? (
              <span key={`gap-${i}`} className="px-2 text-gray-400 text-xs">…</span>
            ) : (
              <button
                key={n}
                onClick={() => setPage(n)}
                className={`min-w-[28px] h-7 px-2 rounded text-xs font-medium transition-colors ${
                  n === page ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {n}
              </button>
            ),
          )}
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="다음 페이지"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {footer && (
        <div className="px-3 py-2 border-t border-gray-100">
          {footer}
        </div>
      )}
    </div>
  );
}
